'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Slightly longer than the 300ms CSS fade, so the panel is fully invisible before it is reset. */
const CLOSE_ANIMATION_MS = 350;

/**
 * Open/close state for the desktop mega-menu.
 *
 * The panels themselves are always in the DOM (see `NavPanel`) — this hook only
 * decides which one is visible, so closing can play its fade-out before the
 * active group is cleared.
 */
export const useDropdownNavigation = () => {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openColumnItem, setOpenColumnItem] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeNow = useCallback(() => {
    setOpenGroup(null);
    setOpenColumnItem(null);
    setIsClosing(false);
  }, []);

  const close = useCallback(() => {
    setIsClosing(true);
    setShouldAnimate(false);

    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    closeTimeout.current = setTimeout(closeNow, CLOSE_ANIMATION_MS);
  }, [closeNow]);

  const toggleGroup = useCallback(
    (groupKey: string) => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);

      // While a panel is fading out its group is still set, so a plain
      // "same title means close" check would swallow the click that is trying
      // to bring it back — press Escape, click the trigger again, nothing.
      const shouldClose = !isClosing && openGroup === groupKey;

      setIsClosing(false);

      if (shouldClose) {
        setOpenGroup(null);
        setOpenColumnItem(null);
        return;
      }

      setOpenGroup(groupKey);
    },
    [isClosing, openGroup],
  );

  const openGroupByKey = useCallback((groupKey: string) => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);

    setIsClosing(false);
    setOpenGroup(groupKey);
  }, []);

  const toggleColumnItem = useCallback((itemKey: string) => {
    setOpenColumnItem((current) => (current === itemKey ? null : itemKey));
  }, []);

  // A different group means a fresh panel: its right-hand column starts on the
  // default sections rather than whatever was selected in the previous one.
  useEffect(() => {
    setOpenColumnItem(null);
  }, [openGroup]);

  useEffect(
    () => () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    },
    [],
  );

  // Deferred by a frame so the panel is painted at opacity 0 before the
  // transition to 1 starts; setting both in one paint skips the fade.
  useEffect(() => {
    if (openGroup && !isClosing) {
      const frame = requestAnimationFrame(() => setShouldAnimate(true));
      return () => cancelAnimationFrame(frame);
    }

    setShouldAnimate(false);
  }, [openGroup, isClosing]);

  return {
    openGroup,
    openColumnItem,
    isClosing,
    shouldAnimate,
    close,
    closeNow,
    toggleGroup,
    openGroupByKey,
    toggleColumnItem,
  };
};

export type DropdownNavigation = ReturnType<typeof useDropdownNavigation>;
